import React, { useEffect, useState } from "react";
import "./Stats.scss";
import "../styles/table.scss";
import axios from "axios";
import DataTable from "react-data-table-component";
import { Link, useHistory } from "react-router-dom";
import TableStyles from "../styles/tableStyles";
import Config from "../Config";
import "react-datepicker/dist/react-datepicker.css";
import { ReactComponent as BackIcon } from "../assets/svg/arrow-circle-left.svg";

import { Colors } from "../utils/Colors";
import Roles from "../utils/Roles";
import { Scopes } from "../models/Scopes";

import { CartesianGrid, Tooltip, YAxis, ResponsiveContainer, BarChart, Bar, Legend, LabelList } from "recharts";
import { useTranslation } from "react-i18next";

enum StatisticPeriod {
  ALL = "all",
  THIS_YEAR = "thisYear",
  THIS_MONTH = "thisMonth",
  THIRTY_DAYS = "thirtyDays",
  FIFTEEN_DAYS = "fifteenDays",
  SEVEN_DAYS = "sevenDays",
  YESTERDAY = "yesterday",
  TODAY = "today",
}

interface PumpingNoteStats {
  totalPumpingNote: any;
  totalDigitalPumpingNote: any;
  organization: string;
  uuid: string;
}

enum Keys {
  totalDigitalPumpingNote = "totalDigitalPumpingNote",
  totalPumpingNote = "totalPumpingNote",
}

const StatsBMD = React.memo((props: any) => {
  useEffect(() => {
    fetchStats(currentTab, props.currentPeriod, currentRegion);
  }, [props.currentPeriod]);

  useEffect(() => {
    fetchStats(currentTab, props.currentPeriod, currentRegion);
  }, []);

  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const history = useHistory();
  const [filteredItems, setFilterereditems] = useState<PumpingNoteStats[]>([]);
  const [currentTab, setCurrentTab] = useState(Roles.isAffiliate() ? "regions" : "sites");
  const [sortDirection] = useState("desc");
  const [sortColumn] = useState("total");

  const [currentRegion, setCurrentRegion] = useState<PumpingNoteStats>();

  const [disabledKeys, setDisabledKeys] = useState<Keys[]>([]);

  const keys = [
    { key: Keys.totalDigitalPumpingNote, label: t("stats.totalDigitalPumpingNote"), color: Colors.graphPrimary },
    {
      key: Keys.totalPumpingNote,
      label: t("table.total"),
      color: Colors.graphPrimaryBis,
    },
  ];

  const openRegionStats = (region: PumpingNoteStats) => {
    setCurrentRegion(region);
    setCurrentTab("sites");
    fetchStats("sites", props.currentPeriod, region);
  };

  const columns: any[] = [
    {
      name: t("name"),

      cell: (row: PumpingNoteStats) => (
        <div
          className={
            row.uuid == null
              ? "firstColumn totalLine"
              : currentTab == "regions"
              ? "firstColumn link primary"
              : "firstColumn"
          }
          onClick={() => (row.uuid != null && currentTab == "regions" ? openRegionStats(row) : null)}
        >
          {row.organization}
        </div>
      ),
      sortable: true,
      selector: (row: PumpingNoteStats) => row.organization,

      right: false,
      id: "organization",
    },
    {
      name: t("table.total"),

      cell: (row: PumpingNoteStats) => (
        <div className={row.uuid == null ? "firstColumn totalLine" : ""}>{row.totalPumpingNote}</div>
      ),
      sortable: true,
      selector: (row: PumpingNoteStats) => row.totalPumpingNote,

      right: false,
      sortFunction: (rowA: PumpingNoteStats, rowB: PumpingNoteStats) => {
        return rowA.totalPumpingNote > rowB.totalPumpingNote ? 1 : -1;
      },
      id: "totalPumpingNote",
    },

    {
      name: t("stats.totalDigitalPumpingNote"),

      cell: (row: PumpingNoteStats) => (
        <div className={row.uuid == null ? "firstColumn totalLine" : ""}>{row.totalDigitalPumpingNote}</div>
      ),
      sortable: true,
      selector: (row: PumpingNoteStats) => row.totalDigitalPumpingNote,

      right: false,
      sortFunction: (rowA: PumpingNoteStats, rowB: PumpingNoteStats) => {
        return rowA.totalDigitalPumpingNote > rowB.totalDigitalPumpingNote ? 1 : -1;
      },
      id: "totalDigitalPumpingNote",
    },
  ];

  if (Roles.hasRight(Scopes.SCOPE_EXTRANET_PUMPING_NOTE_VIEW) && currentTab == "sites" && Roles.hasBMD()) {
    columns.unshift({
      name: "",
      sortable: true,
      cell: (row: PumpingNoteStats) =>
        row.organization == "Total" || row.uuid == null ? null : (
          <Link className="table-link" to={seeSiteBMD(row)}>
            {t("seeBMD")}
          </Link>
        ),
      button: true,
    });
  }

  if (currentTab == "sites") {
    columns.unshift({
      name: "",
      sortable: true,
      cell: (row: any) =>
        row.uuid == null ? null : (
          <Link className="table-link" to={seeSiteDns(row)}>
            {t("seeDns")}
          </Link>
        ),
      button: true,
    });
  }

  const seeSiteDns = (stats: PumpingNoteStats) => {
    let url = "deliverynotes?site=" + stats.organization;
    return url;
  };
  const seeSiteBMD = (stats: PumpingNoteStats) => {
    let url = "pumpingNotes?site=" + stats.organization;
    return url;
  };

  const fetchStats = (tab: string, period: StatisticPeriod, region: PumpingNoteStats | undefined = undefined) => {
    setIsLoading(true);
    setFilterereditems([]);

    const token = localStorage.getItem("token");

    let dataToSend: any = {};

    dataToSend.period = period;

    if (region != null) {
      dataToSend.region = region.uuid;
      setCurrentRegion(region);
      props.onUpdateCurrentRegion(region);
    }
    axios
      .post(Config.getUrl("stats/pumping-notes/" + tab), dataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        setFilterereditems(res.data.content);
        if (res.data.content?.length > 0) {
          // setLastUpdate(res.data.content[0].lastUpdate);
          props.onUpdateLastUpdate(res.data.content[0].lastUpdate);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        if (error.response) {
          if (error.response.status == 401) {
            history.push("/");
          }
        }
      });
  };
  const getSortedArray = (): PumpingNoteStats[] => {
    return filteredItems.sort((rowA: PumpingNoteStats, rowB: PumpingNoteStats) => {
      return rowA.totalPumpingNote > rowB.totalPumpingNote ? -1 : 1;
    });
  };

  const getData = () => {
    let labels = Array.from(
      getSortedArray().filter((f: PumpingNoteStats) => f.uuid != null),
      (x) => x.organization
    );

    let totalDigitalPumpingNote = Array.from(
      getSortedArray().filter((f: PumpingNoteStats) => f.uuid != null),
      (x) => x.totalDigitalPumpingNote
    );

    let totalPumpingNote = Array.from(
      getSortedArray().filter((f: PumpingNoteStats) => f.uuid != null),
      (x) => x.totalPumpingNote - x.totalDigitalPumpingNote
    );

    let data: any[] = [];
    for (let i = 0; i < getSortedArray().length; i++) {
      let newData = {
        name: labels[i],
        totalDigitalPumpingNote: totalDigitalPumpingNote[i],
        totalPumpingNote: totalPumpingNote[i],
      };
      data.push(newData);
    }

    return data;
  };

  const isInactive = (dataKey: Keys) => {
    return disabledKeys.filter((k) => k === dataKey).length > 0;
  };

  const renderCusomizedLegend = () => {
    return (
      <div className="customized-legend">
        {keys.map((key: any) => {
          return (
            <span
              key={key.key}
              className="legend-item"
              onClick={() => handleLegendClick(key.key)}
              style={{ marginRight: 10 }}
            >
              <div className="legend-icon" style={{ backgroundColor: isInactive(key.key) ? "#AAA" : key.color }}></div>

              <div style={{ color: isInactive(key.key) ? "#AAA" : key.color }}>{key.label}</div>
            </span>
          );
        })}
      </div>
    );
  };

  const handleLegendClick = (key: Keys) => {
    if (disabledKeys.filter((k) => k === key).length > 0) {
      let newKeys = [...disabledKeys];
      newKeys = newKeys.filter((k) => k !== key);
      setDisabledKeys(newKeys);
    } else {
      setDisabledKeys([...disabledKeys, key]);
    }
  };

  const getPayload = (data: any, dataKey: string) => {
    const { payload } = data;
    if (payload.filter((p: any) => p.dataKey === dataKey).length > 0) {
      return payload.filter((p: any) => p.dataKey === dataKey)[0];
    } else {
      return null;
    }
  };
  const CustomTooltip = (data: any) => {
    if (data.active && data.payload && data.payload.length > 0) {
      return (
        <div className="custom-tooltip">
          <div>{getData()[data.label].name}</div>
          {getPayload(data, Keys.totalDigitalPumpingNote) != null ? (
            <p className="tooltip-value body-s-medium" style={{ color: Colors.graphPrimary }}>
              {`${getPayload(data, Keys.totalDigitalPumpingNote).name}`} :{" "}
              {`${getPayload(data, Keys.totalDigitalPumpingNote).value}`}
            </p>
          ) : null}

          {getPayload(data, Keys.totalPumpingNote) != null ? (
            <p className="tooltip-value body-s-medium" style={{ color: Colors.graphPrimaryBis }}>
              {`${getPayload(data, Keys.totalPumpingNote).name}`} :{" "}
              {`${
                getPayload(data, Keys.totalDigitalPumpingNote) != null
                  ? getPayload(data, Keys.totalPumpingNote).value + getPayload(data, Keys.totalDigitalPumpingNote).value
                  : getPayload(data, Keys.totalPumpingNote).value
              }`}
            </p>
          ) : null}

          {getPayload(data, "deliveredDigitalDeliveryNote") != null ? (
            <p className="tooltip-value body-s-medium" style={{ color: Colors.graphSecondary }}>
              {`${getPayload(data, "deliveredDigitalDeliveryNote").name}`} :{" "}
              {`${getPayload(data, "deliveredDigitalDeliveryNote").value}`}
            </p>
          ) : null}
          {getPayload(data, "totaldeliveredDeliveryNote") != null ? (
            <p className="tooltip-value body-s-medium" style={{ color: Colors.graphSecondaryBis }}>
              {`${getPayload(data, "totaldeliveredDeliveryNote").name}`} :{" "}
              {`${
                getPayload(data, "deliveredDigitalDeliveryNote") != null
                  ? getPayload(data, "deliveredDigitalDeliveryNote").value +
                    getPayload(data, "totaldeliveredDeliveryNote").value
                  : getPayload(data, "totaldeliveredDeliveryNote").value
              }`}
            </p>
          ) : null}
          {getPayload(data, "collectedDigitalDeliveryNote") != null ? (
            <p className="tooltip-value body-s-medium" style={{ color: Colors.graphThird }}>
              {`${getPayload(data, "collectedDigitalDeliveryNote").name}`} :{" "}
              {`${getPayload(data, "collectedDigitalDeliveryNote").value}`}
            </p>
          ) : null}
          {getPayload(data, "totalCollectedDeliveryNote") != null ? (
            <p className="tooltip-value body-s-medium" style={{ color: Colors.graphThirdBis }}>
              {`${getPayload(data, "totalCollectedDeliveryNote").name}`} :{" "}
              {`${
                getPayload(data, "collectedDigitalDeliveryNote") != null
                  ? getPayload(data, "collectedDigitalDeliveryNote").value +
                    getPayload(data, "totalCollectedDeliveryNote").value
                  : getPayload(data, "totalCollectedDeliveryNote").value
              }`}
            </p>
          ) : null}
        </div>
      );
    }

    return null;
  };

  const toggleTab = (tab: string) => {
    setCurrentTab(tab);
    fetchStats(tab, props.currentPeriod);
  };

  const renderCustomizedLabel = (labelProps: any) => {
    const { x, y, width, value, index } = labelProps;
    const radius = 10;
    return (
      <g>
        <text x={x + width / 2} y={y - radius - index * 2} fill="#000000" textAnchor="middle" dominantBaseline="middle">
          {value}
        </text>
      </g>
    );
  };

  return (
    <div className={" table-container"}>
      {Roles.isAffiliate() && currentTab == "sites" ? (
        <div className="returnBtn" onClick={() => toggleTab("regions")} style={{ marginBottom: 30 }}>
          <BackIcon className="icon" width="25" height="20"></BackIcon>
          <span style={{ marginLeft: 10 }}>Retour</span>
        </div>
      ) : null}

      <ResponsiveContainer width="100%" height={500}>
        <BarChart
          width={500}
          height={600}
          data={getData()}
          style={{ paddingTop: 20 }}
          margin={{
            top: 30,
            right: 0,
            left: 0,
            bottom: 60,
          }}
        >
          <Legend
            margin={{ top: 1000, bottom: 1000 }}
            wrapperStyle={{ top: -10, marginBottom: 100 }}
            verticalAlign="top"
            content={renderCusomizedLegend}
          />
          <CartesianGrid strokeDasharray="1 1" />
          <YAxis stroke={Colors.grey500} />
          <Tooltip cursor={{ fill: "none" }} content={<CustomTooltip />} />
          {isInactive(Keys.totalDigitalPumpingNote) ? null : (
            <Bar
              isAnimationActive={false}
              name={t("stats.totalDigitalPumpingNote")}
              dataKey="totalDigitalPumpingNote"
              stackId="a"
              fill={Colors.graphPrimary}
            />
          )}
          {isInactive(Keys.totalPumpingNote) ? null : (
            <Bar
              isAnimationActive={false}
              name={t("table.total")}
              dataKey="totalPumpingNote"
              stackId="a"
              fill={Colors.graphPrimaryBis}
            >
              <LabelList dataKey="name" position="top" content={renderCustomizedLabel} />
            </Bar>
          )}
        </BarChart>
      </ResponsiveContainer>

      {isLoading ? (
        <div>{t("loading")}</div>
      ) : (
        <DataTable
          className="table"
          noHeader={true}
          customStyles={TableStyles}
          columns={columns}
          data={filteredItems}
          noDataComponent={<div className="noResults">{t("noSite")}</div>}
          defaultSortAsc={sortDirection == "asc"}
          defaultSortFieldId={sortColumn}
        />
      )}
    </div>
  );
});

export default StatsBMD;
